import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/api';
import { 
  User, 
  Mail, 
  Camera, 
  Trash2, 
  Save, 
  Calendar, 
  Shield, 
  AlertCircle, 
  CheckCircle,
  UploadCloud 
} from 'lucide-react';
import '../styles/profile.css';

const Profile = () => {
  const { user, updateUser } = useAuth();
  
  // Form states
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  
  // Status states
  const [formLoading, setFormLoading] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const fileInputRef = useRef(null);

  // Synchronize state with current user info on mount/change
  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setEmail(user.email || '');
    }
  }, [user]);

  if (!user) {
    return (
      <div className="profile-container">
        <div className="profile-background"></div>
        <div className="profile-content">
          <div className="profile-card" style={{ textAlign: 'center' }}>
            <AlertCircle size={48} className="text-red-500" style={{ margin: '0 auto 1rem' }} />
            <p>Please log in to manage your profile.</p>
          </div>
        </div>
      </div>
    );
  }

  // Handle name and email updates
  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    // Client-side validations
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    if (name.trim().length < 2) {
      setError('Name must be at least 2 characters.');
      return;
    }
    if (!email.trim()) {
      setError('Email is required.');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setFormLoading(true);
    try {
      const updatedProfile = await apiService.updateUserProfile({
        name: name.trim(),
        email: email.trim()
      });

      // Update local and context state
      updateUser({
        name: updatedProfile.name,
        email: updatedProfile.email
      });

      setSuccess('Profile updated successfully.');
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      console.error('Profile update error:', err);
      setError(err.message || 'An error occurred while updating profile.');
    } finally {
      setFormLoading(false);
    }
  };

  // Handle profile image file change
  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setError('');
    setSuccess('');

    // Validate size (1MB limit)
    if (file.size > 1 * 1024 * 1024) {
      setError('Image size must be less than 1MB.');
      return;
    }

    // Validate extensions
    const allowedExtensions = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/jpg'];
    if (!allowedExtensions.includes(file.type)) {
      setError('Invalid file type. Only JPG, JPEG, PNG, WEBP, and GIF are allowed.');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    setImageLoading(true);
    try {
      const updatedProfile = await apiService.uploadProfileImage(formData);
      
      // Update local and context state
      updateUser({
        profileImageUrl: updatedProfile.profileImageUrl
      });

      setSuccess('Profile image updated successfully.');
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      console.error('Image upload error:', err);
      setError(err.message || 'An error occurred while uploading profile image.');
    } finally {
      setImageLoading(false);
      // Reset input value to allow selecting same file again
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Handle profile image deletion
  const handleDeleteImage = async () => {
    if (!window.confirm('Are you sure you want to remove your profile image?')) {
      return;
    }

    setError('');
    setSuccess('');
    setImageLoading(true);

    try {
      await apiService.deleteProfileImage();
      
      // Update local and context state (set to null)
      updateUser({
        profileImageUrl: null
      });

      setSuccess('Profile image removed successfully.');
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      console.error('Image delete error:', err);
      setError(err.message || 'An error occurred while removing profile image.');
    } finally {
      setImageLoading(false);
    }
  };

  // Format signup date
  const formatJoinedDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const options = { year: 'numeric', month: 'long', day: 'numeric' };
      return new Date(dateString).toLocaleDateString(undefined, options);
    } catch (e) {
      return 'N/A';
    }
  };

  // Get initials for placeholder avatar
  const getInitials = (userName) => {
    if (!userName) return 'U';
    return userName
      .split(' ')
      .map(n => n[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  };

  return (
    <div className="profile-container">
      <div className="profile-background"></div>
      <div className="profile-glow glow-1"></div>
      <div className="profile-glow glow-2"></div>

      <div className="profile-content">
        <div className="profile-card">
          
          <div className="profile-header">
            <h1 className="profile-title">Account Profile</h1>
            <p className="profile-subtitle">Manage your personal information and profile picture</p>
          </div>

          <div className="profile-body">
            
            {/* Left: Avatar Section */}
            <div className="profile-avatar-column">
              <div className="profile-avatar-container">
                <div className="profile-avatar-inner">
                  {imageLoading && (
                    <div className="profile-avatar-loading">
                      <div className="profile-avatar-spinner"></div>
                    </div>
                  )}
                  {user.profileImageUrl ? (
                    <img 
                      src={user.profileImageUrl} 
                      alt={user.name} 
                      className="profile-avatar-image"
                    />
                  ) : (
                    <div className="profile-avatar-placeholder">
                      {getInitials(user.name)}
                    </div>
                  )}
                  
                  {/* Hover Overlay Trigger */}
                  <div 
                    className="profile-avatar-overlay" 
                    onClick={() => !imageLoading && fileInputRef.current.click()}
                  >
                    <Camera />
                    <span>Change</span>
                  </div>
                </div>
              </div>

              {/* Hidden file input */}
              <input 
                type="file" 
                ref={fileInputRef} 
                onChange={handleImageChange} 
                accept=".jpg,.jpeg,.png,.webp,.gif"
                style={{ display: 'none' }}
              />

              <div className="profile-avatar-actions">
                <button 
                  type="button" 
                  className="btn-upload-label"
                  onClick={() => fileInputRef.current.click()}
                  disabled={imageLoading}
                >
                  <UploadCloud size={14} />
                  Upload Photo
                </button>
                
                {user.profileImageUrl && (
                  <button 
                    type="button" 
                    className="btn-delete-avatar"
                    onClick={handleDeleteImage}
                    disabled={imageLoading}
                  >
                    <Trash2 size={14} />
                    Remove
                  </button>
                )}
              </div>
            </div>

            {/* Right: Form Section */}
            <div className="profile-form-column">
              
              {error && (
                <div className="profile-alert alert-error">
                  <AlertCircle size={18} />
                  <span>{error}</span>
                </div>
              )}

              {success && (
                <div className="profile-alert alert-success">
                  <CheckCircle size={18} />
                  <span>{success}</span>
                </div>
              )}

              <form onSubmit={handleUpdateProfile} className="profile-form-column" style={{ gap: '1.25rem' }}>
                <div className="profile-form-group">
                  <label htmlFor="profile-name">
                    <User size={16} />
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="profile-name"
                    className="profile-form-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter your full name"
                    disabled={formLoading}
                  />
                </div>

                <div className="profile-form-group">
                  <label htmlFor="profile-email">
                    <Mail size={16} />
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="profile-email"
                    className="profile-form-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    disabled={formLoading}
                  />
                </div>

                {/* Account Details Read-Only Grid */}
                <div className="profile-meta-row">
                  <div className="profile-meta-item">
                    <span className="meta-label">
                      <Shield size={12} style={{ display: 'inline', marginRight: '4px', verticalAlign: 'text-top' }} />
                      Role
                    </span>
                    <span className="meta-value">{user.role}</span>
                  </div>
                  <div className="profile-meta-item">
                    <span className="meta-label">
                      <Calendar size={12} style={{ display: 'inline', marginRight: '4px', verticalAlign: 'text-top' }} />
                      Member Since
                    </span>
                    <span className="meta-value">{formatJoinedDate(user.createdAt)}</span>
                  </div>
                </div>

                <div className="profile-actions">
                  <button 
                    type="submit" 
                    className="btn-profile-save"
                    disabled={formLoading || imageLoading}
                  >
                    {formLoading ? (
                      <div className="profile-spinner"></div>
                    ) : (
                      <>
                        <Save size={16} />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </form>

            </div>

          </div>

        </div>
      </div>
    </div>
  );
};

export default Profile;
